import { useEffect, useReducer } from 'react';
import { useFetch } from '@/hooks/use-fetch';
import { useWorkspace } from '@/contexts/workspace-context';

// dispatch is not tracked by react-hooks/exhaustive-deps or
// react-hooks/rules-of-hooks in the same way useState's setter is —
// a synchronous dispatch({ type: 'reset' }) at the top of a useEffect
// is legal. Mirror the pattern from use-dashboard-metrics.ts.
type State = { show: boolean; loading: boolean };
type Action = { type: 'reset' } | { type: 'resolve'; show: boolean };

function reducer(_state: State, action: Action): State {
  switch (action.type) {
    case 'reset':
      // Synchronous reset on workspace change: never carry a prior 'hidden'
      // into the next fetch. Default visible so it never flickers out.
      return { show: true, loading: true };
    case 'resolve':
      return { show: action.show, loading: false };
  }
}

/**
 * Returns sidebar visibility state for the Launchpad item.
 *
 * Fail-visible: while loading OR on error, show === true.
 * Hidden only when GET /launchpad responds ok and allComplete === true.
 */
export function useLaunchpadVisibility(): { show: boolean; loading: boolean } {
  const fetchWithAuth = useFetch();
  const { activeWorkspace } = useWorkspace();
  // GET /launchpad is workspace-scoped (X-Workspace-Id header, attached by
  // use-fetch only once a workspace is active). Gate on the id so the request
  // never fires header-less and 400s with WORKSPACE_ID_REQUIRED while the
  // workspace context is still resolving.
  const wsId = activeWorkspace?.id;
  const [state, dispatch] = useReducer(reducer, { show: true, loading: true });

  useEffect(() => {
    if (!wsId) return; // wait for the workspace; fail-visible default holds
    let active = true;
    dispatch({ type: 'reset' });
    (async () => {
      try {
        // Non-critical sidebar probe: opt out of the global ErrorPage so a
        // /launchpad 5xx/network blip stays fail-visible here instead of
        // nuking the whole authenticated app.
        const res = await fetchWithAuth('/launchpad', undefined, { suppressGlobalError: true });
        if (res.ok) {
          const s = (await res.json()) as { allComplete: boolean };
          if (active) dispatch({ type: 'resolve', show: !s.allComplete });
        } else if (active) {
          // Non-2xx (e.g. 422): fail-visible — keep showing the item
          dispatch({ type: 'resolve', show: true });
        }
      } catch {
        // Network error: fail-visible — keep showing the item
        if (active) dispatch({ type: 'resolve', show: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchWithAuth, wsId]);

  return state;
}
